/**
 * The Teams screen, without React.
 *
 * The same draft-then-Save shape as the other Configuration screens: a row is
 * moved in a draft, the draft says what changed, and one Save sends the whole
 * table.
 *
 * Two facts per person, and they are not the same fact. Being on a team is
 * whose job the work is. Leading it is whose people they are — and a lead who
 * is not a member is not a state anybody can act on, so the model never
 * produces one.
 */

import type {
  CreateTeamRequest,
  SaveTeamMembersRequest,
  TeamMemberItem,
} from "@wallyo/contracts/console/configuration";

import { t } from "../../i18n";
import { REASON_MIN_LENGTH } from "../team/team-writes";

/** What a moved row says now. Absent means it has not been touched. */
export type MembershipRow = { member: boolean; leads: boolean };

export type TeamsDraft = Readonly<Record<string, MembershipRow>>;

export const EMPTY_TEAMS_DRAFT: TeamsDraft = {};

/**
 * The two lists as one table, each row knowing which list it came from.
 *
 * The server sends them apart because they mean different things; the table
 * renders them together, so the flag travels with the row rather than being
 * re-derived from an array lookup on every keystroke.
 */
export function withLoaded(
  members: readonly TeamMemberItem[],
  available: readonly TeamMemberItem[],
): readonly LoadedMember[] {
  return [
    ...members.map((each) => ({ ...each, onTeam: true })),
    ...available.map((each) => ({ ...each, onTeam: false })),
  ];
}

export type LoadedMember = TeamMemberItem & { onTeam: boolean };

/** What the row reads as now: the draft when it moved, what loaded otherwise. */
export function rowOf(person: LoadedMember, draft: TeamsDraft): MembershipRow {
  return draft[person.staffMemberId] ?? { member: person.onTeam, leads: person.leads };
}

export function isChanged(person: LoadedMember, draft: TeamsDraft): boolean {
  const row = draft[person.staffMemberId];
  if (row === undefined) return false;
  return row.member !== person.onTeam || row.leads !== person.leads;
}

/**
 * Moves whether somebody is on the team.
 *
 * Taking them off takes the lead with it: a lead who is not a member is not a
 * fact, and leaving the flag set would send the server a row it refuses.
 */
export function withMember(draft: TeamsDraft, person: LoadedMember, member: boolean): TeamsDraft {
  return settle(draft, person, { member, leads: member ? rowOf(person, draft).leads : false });
}

/** Moves whether somebody leads it. Making somebody a lead puts them on the team. */
export function withLeads(draft: TeamsDraft, person: LoadedMember, leads: boolean): TeamsDraft {
  return settle(draft, person, { member: leads ? true : rowOf(person, draft).member, leads });
}

/** A row back where it started is not a change, so it leaves the draft. */
function settle(draft: TeamsDraft, person: LoadedMember, next: MembershipRow): TeamsDraft {
  const { [person.staffMemberId]: _dropped, ...rest } = draft;
  if (next.member === person.onTeam && next.leads === person.leads) return rest;
  return { ...rest, [person.staffMemberId]: next };
}

export function changedRows(people: readonly LoadedMember[], draft: TeamsDraft): readonly LoadedMember[] {
  return people.filter((person) => isChanged(person, draft));
}

export function saveRequest(
  people: readonly LoadedMember[],
  draft: TeamsDraft,
  reason: string,
  version: number,
): SaveTeamMembersRequest {
  return {
    changes: changedRows(people, draft).map((person) => {
      const row = rowOf(person, draft);
      return { staffMemberId: person.staffMemberId, member: row.member, leads: row.leads };
    }),
    reason: reason.trim(),
    version,
  };
}

/** What stops a Save, said before it is sent rather than after it is refused. */
export function saveProblem(
  people: readonly LoadedMember[],
  draft: TeamsDraft,
  reason: string,
  editable: boolean,
): string | null {
  if (!editable) return t("teams.locked");
  const changed = changedRows(people, draft);
  if (changed.length === 0) return t("features.save.nothing");
  // Somebody deactivated is shown so a lead can take them off, never added.
  if (changed.some((person) => !person.active && rowOf(person, draft).member)) return t("teams.gone");
  if (reason.trim().length < REASON_MIN_LENGTH) return t("features.save.reasonNeeded");
  return null;
}

/** Who leads the team once the draft is applied, for the heading. */
export function leadsAfter(people: readonly LoadedMember[], draft: TeamsDraft): readonly string[] {
  return people.filter((person) => rowOf(person, draft).leads).map((person) => person.name);
}

// ── Creating a team, with its people ─────────────────────────────────────
//
// A team with nobody on it owns no work and covers nothing, and whoever
// creates one already knows who it is for. So the people are picked here and
// saved with it, in one transaction.

/** Who has been picked for a team that does not exist yet. */
export type NewTeamDraft = Readonly<Record<string, { member: boolean; leads: boolean }>>;

export const EMPTY_NEW_TEAM: NewTeamDraft = {};

export function pickedRow(staffMemberId: string, draft: NewTeamDraft): MembershipRow {
  return draft[staffMemberId] ?? { member: false, leads: false };
}

/** Off takes the lead with it: nobody leads a team they are not on. */
export function withPickedMember(draft: NewTeamDraft, staffMemberId: string, member: boolean): NewTeamDraft {
  const row = pickedRow(staffMemberId, draft);
  if (!member) {
    const { [staffMemberId]: _dropped, ...rest } = draft;
    return rest;
  }
  return { ...draft, [staffMemberId]: { member: true, leads: row.leads } };
}

/** On brings membership with it, for the same reason. */
export function withPickedLead(draft: NewTeamDraft, staffMemberId: string, leads: boolean): NewTeamDraft {
  const row = pickedRow(staffMemberId, draft);
  if (!leads && !row.member) {
    const { [staffMemberId]: _dropped, ...rest } = draft;
    return rest;
  }
  return { ...draft, [staffMemberId]: { member: leads ? true : row.member, leads } };
}

export function pickedMembers(draft: NewTeamDraft): readonly { staffMemberId: string; leads: boolean }[] {
  return Object.entries(draft)
    .filter(([, row]) => row.member)
    .map(([staffMemberId, row]) => ({ staffMemberId, leads: row.leads }));
}

export function createTeamRequest(
  name: string,
  purpose: string,
  reason: string,
  draft: NewTeamDraft,
): CreateTeamRequest {
  return {
    name: name.trim(),
    purpose: purpose.trim() || null,
    members: pickedMembers(draft),
    reason: reason.trim(),
  };
}

/**
 * What stops the form.
 *
 * A team with nobody on it is allowed: you may stand one up before anybody is
 * free to join it. A team with a lead and no members is not, and cannot be
 * produced here anyway — the two are kept in step above.
 */
export function createTeamProblem(name: string, reason: string): string | null {
  if (name.trim().length === 0) return t("teams.new.nameNeeded");
  if (reason.trim().length < REASON_MIN_LENGTH) return t("features.save.reasonNeeded");
  return null;
}
